// Shared budget + CAC math for the Marketing section.
//
// Extracted from BudgetView so the marketing dashboard KPI tiles and the
// Budget & CAC screen compute identical numbers from the same inputs
// (the /api/data/marketing/summary payload + the per-class BWM P&L from
// FinancialDataContext). If the two screens ever disagree, the bug is
// here, not in two divergent copies.

export const MONTH_KEYS = ['jan','feb','mar','apr','may','jun','jul','aug','sep','oct','nov','dec'];

// BWM-only revenue lines (per the marketing budget convention: subscription,
// investment management, tax planning — exclude tax_preparation [BTS] and
// commissions [BIA]).
export const BWM_REVENUE_LINES = ['subscription_fees', 'investment_management', 'tax_planning'];

// Hard-dollar marketing lines we pull from the per-class derived P&L.
// referral_fees lives under cogs; advertising_promotional under expenses.
export function bwmHardDollarsForMonth(bwmMonth) {
  const referralFees = Number(bwmMonth?.cogs?.referral_fees) || 0;
  const advertising = Number(bwmMonth?.expenses?.advertising_promotional) || 0;
  return { referralFees, advertising, hardDollars: referralFees + advertising };
}

export function bwmRevenueForMonth(bwmMonth) {
  if (!bwmMonth?.income) return 0;
  return BWM_REVENUE_LINES.reduce((s, k) => s + (Number(bwmMonth.income[k]) || 0), 0);
}

// Build the combined dataset: server-side soft $ + CAC count, frontend-side
// BWM revenue + BWM hard $. Returns null until both inputs are loaded.
export function combineBudgetSummary(summary, bwmPL) {
  if (!summary || !bwmPL?.months) return null;

  const monthly = MONTH_KEYS.map((mk, i) => {
    const m = bwmPL.months[mk] || {};
    const revenue = bwmRevenueForMonth(m);
    const hard = bwmHardDollarsForMonth(m);
    const soft = summary.softByMonth?.[i] || 0;
    return {
      mk,
      revenue,
      referralFees: hard.referralFees,
      advertising: hard.advertising,
      hardDollars: hard.hardDollars,
      softDollars: soft,
      totalSpend: hard.hardDollars + soft,
    };
  });

  const annualRevenue = monthly.reduce((s, m) => s + m.revenue, 0);
  const annualHard = monthly.reduce((s, m) => s + m.hardDollars, 0);
  const annualSoft = summary.annualSoft || 0;
  const annualMarketingSoft = summary.annualMarketingSoft || 0;
  const annualSalesSoft = summary.annualSalesSoft || 0;
  const annualTotal = annualHard + annualSoft;
  const annualPct = annualRevenue > 0 ? annualTotal / annualRevenue : 0;
  const goalDollars = Math.round(annualRevenue * summary.goalPercent);
  const goalGap = goalDollars - annualTotal;

  const cur = monthly[summary.monthIdx] || {};

  // CAC split: marketing CAC = (hard + marketing soft) / wins,
  //            sales CAC = sales soft / wins,
  //            total CAC = total / wins.
  // Hard $ is treated as marketing-side (referral fees + advertising
  // are demand-generation, not sales conversion).
  const marketingTotal = annualHard + annualMarketingSoft;
  const cac = summary.wonCount > 0 ? {
    marketing: Math.round(marketingTotal / summary.wonCount),
    sales: Math.round(annualSalesSoft / summary.wonCount),
    total: Math.round(annualTotal / summary.wonCount),
  } : { marketing: null, sales: null, total: null };

  return {
    year: summary.year,
    goalPercent: summary.goalPercent,
    monthIdx: summary.monthIdx,
    annual: {
      revenue: Math.round(annualRevenue),
      hardDollars: Math.round(annualHard),
      softDollars: Math.round(annualSoft),
      totalSpend: Math.round(annualTotal),
      pctOfRevenue: annualPct,
      goalGap: Math.round(goalGap),
    },
    currentMonth: {
      index: summary.monthIdx,
      revenue: Math.round(cur.revenue || 0),
      hardDollars: Math.round(cur.hardDollars || 0),
      softDollars: Math.round(cur.softDollars || 0),
      totalSpend: Math.round((cur.hardDollars || 0) + (cur.softDollars || 0)),
    },
    monthly: monthly.map(m => ({
      ...m,
      revenue: Math.round(m.revenue),
      referralFees: Math.round(m.referralFees),
      advertising: Math.round(m.advertising),
      hardDollars: Math.round(m.hardDollars),
      softDollars: Math.round(m.softDollars),
      totalSpend: Math.round(m.totalSpend),
    })),
    byActivity: summary.byActivity || [],
    byContent: summary.byContent || [],
    cac: {
      wonCount: summary.wonCount,
      annualSpend: Math.round(annualTotal),
      marketingSpend: Math.round(marketingTotal),
      salesSpend: Math.round(annualSalesSoft),
      marketingCac: cac.marketing,
      salesCac: cac.sales,
      totalCac: cac.total,
    },
    annualMarketingSoft: Math.round(annualMarketingSoft),
    annualSalesSoft: Math.round(annualSalesSoft),
    wonBySource: summary.wonBySource || [],
  };
}

// ─── LTV ────────────────────────────────────────────────────────────
//
// LTV per active BWM client = (monthlyRevenue x 12 x retentionYears)
//                           + (investmentFeeAnnual x retentionYears)
//                           + taxPlanFee (one-time)
//
// retentionYears defaults to 2. As churn data accumulates, this should
// switch to actual avg tenure of churned clients (TODO: compute from
// churned BWM clients with full lifecycle data once we have ≥10 wins).
export const LTV_DEFAULT_RETENTION_YEARS = 2;

export function computeLtvStats(clients, totalCac) {
  const yrs = LTV_DEFAULT_RETENTION_YEARS;
  const activeBwm = (clients || []).filter(c =>
    c.division === 'bwm' && c.status === 'Active'
  );

  // Per-client LTV (recurring annualized x years + one-time fees)
  let totalLtv = 0;
  const byTier = {};
  for (const c of activeBwm) {
    const monthlyAnnualized = (Number(c.monthlyRevenue) || 0) * 12;
    const annualBase = monthlyAnnualized || (Number(c.annualRevenue) || 0);
    const investmentRecurring = Number(c.investmentFeeAnnual) || 0;
    const taxPlanOneTime = Number(c.taxPlanFee) || 0;
    const clientLtv = (annualBase + investmentRecurring) * yrs + taxPlanOneTime;
    totalLtv += clientLtv;

    const tier = c.serviceTier || 'unknown';
    if (!byTier[tier]) byTier[tier] = { count: 0, totalLtv: 0 };
    byTier[tier].count += 1;
    byTier[tier].totalLtv += clientLtv;
  }

  const avgLtv = activeBwm.length > 0 ? totalLtv / activeBwm.length : 0;
  const ratio = (totalCac && totalCac > 0) ? avgLtv / totalCac : null;

  return {
    retentionYears: yrs,
    activeCount: activeBwm.length,
    avgLtv: Math.round(avgLtv),
    totalPortfolioLtv: Math.round(totalLtv),
    byTier: Object.entries(byTier)
      .map(([tier, v]) => ({
        tier,
        count: v.count,
        avgLtv: v.count > 0 ? Math.round(v.totalLtv / v.count) : 0,
      }))
      .sort((a, b) => b.avgLtv - a.avgLtv),
    ratio,
  };
}
